import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { useAuth } from '../context/AuthContext';
import { useSales } from '../hooks/useSales';
import { ILS, formatRelativeDate, buildWhatsAppText, computeSale, isoToday, toLocalDateStr, getSaleDogs, matchesSaleSearch } from '../utils/salesUtils';
import '../styles/pages/sales.css';

const AVATAR_COLORS = ['dog', 'dog2', 'dog3', 'dog4', 'dog5'];
const avatarColor = (str) => AVATAR_COLORS[(str?.charCodeAt?.(0) || 0) % AVATAR_COLORS.length];

const FILTERS = [
  { key: 'paid',    label: 'Pagados' },
  { key: 'partial', label: 'Parcial' },
  { key: 'due',     label: 'Pendiente' },
];

const PAYMENT_FILTERS_UI = [
  { key: 'Cash',     label: 'Efectivo' },
  { key: 'Card',     label: 'Tarjeta' },
  { key: 'Transfer', label: 'Transferencia' },
];

const STATUS_LABELS = {
  paid:      'Pagado',
  partial:   'Parcial',
  due:       'Pendiente',
  draft:     'Borrador',
  cancelled: 'Cancelada',
};

const shiftDate = (isoDate, days) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
};

const formatDayLabel = (isoDate) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const selected = new Date(y, m - 1, d, 0, 0, 0, 0);
  const diffDays = Math.round((today - selected) / 86400000);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  return selected.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function Sales() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { sales, allSales, loading, filterType, setFilterType, paymentFilter, setPaymentFilter, summary, loadSales, page, setPage, totalPages, counts } = useSales();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(isoToday);

  const isToday = selectedDate === isoToday();
  const isStatusFiltered = filterType !== 'all';
  const isPaymentFiltered = paymentFilter !== 'all';
  const isSearching = searchQuery.trim().length > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = { page, limit: 20 };
      if (isSearching) params.search = searchQuery;
      if (isStatusFiltered) params.status = filterType;
      if (isPaymentFiltered) params.payment = paymentFilter;
      if (!isStatusFiltered && !isPaymentFiltered && !isSearching) params.date = selectedDate;
      
      loadSales(params);
    }, 300);
    return () => clearTimeout(timer);
  }, [page, searchQuery, filterType, paymentFilter, selectedDate, loadSales, isSearching, isStatusFiltered, isPaymentFiltered]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterType, paymentFilter, selectedDate, setPage]);

  const displaySales = sales;

  return (
    <div className="pos-page pos-scope">
      <Navigation />

      {/* Top bar */}
      <div className="pos-topbar">
        <div style={{ width: 38 }} />
        <h1>Ventas</h1>
        <button
          className="pos-icon-btn"
          onClick={() => navigate('/pos/new')}
          aria-label="Nueva venta"
        >
          ＋
        </button>
      </div>

      {/* Stat tiles — admin only */}
      {role === 'admin' && (
        <div className="pos-stat-grid">
          <div className="pos-stat-card">
            <div className="pos-stat-label">Hoy</div>
            <div className="pos-stat-value accent">{ILS(summary.todayRevenue)}</div>
            <div className="pos-stat-sub">{summary.todayCount} ventas</div>
          </div>
          <div
            className="pos-stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => setFilterType(prev => (prev === 'due' || prev === 'partial') ? 'all' : 'due')}
            title="Ver ventas pendientes"
          >
            <div className="pos-stat-label">Pendiente</div>
            <div className="pos-stat-value warm">{ILS(summary.outstanding)}</div>
            <div className="pos-stat-sub">{summary.outstandingCount} sin cobrar</div>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="pos-search-wrap">
        <span className="pos-search-icon">🔍</span>
        <input
          className="pos-search-input"
          type="text"
          placeholder="Buscar por perro, dueño, #venta o producto (ej. transporte, vacuna)…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="pos-search-clear" onClick={() => setSearchQuery('')}>✕</button>
        )}
      </div>

      {/* Day navigation — hidden when any filter is active */}
      {!isStatusFiltered && !isPaymentFiltered && (
        <div className="pos-day-nav">
          <button className="pos-day-nav-btn" onClick={() => setSelectedDate(d => shiftDate(d, -1))}>‹</button>
          <button
            className={`pos-day-nav-label${isToday ? ' today' : ''}`}
            onClick={() => setSelectedDate(isoToday())}
          >
            {formatDayLabel(selectedDate)}
            <span className="pos-day-nav-date">{selectedDate.slice(8, 10)}/{selectedDate.slice(5, 7)}/{selectedDate.slice(0, 4)}</span>
          </button>
          <button className="pos-day-nav-btn" onClick={() => setSelectedDate(d => shiftDate(d, 1))} disabled={isToday}>›</button>
        </div>
      )}

      {/* Filter chips — status + payment (same line) */}
      <div className="pos-filter-chips">
        {FILTERS.map(f => {
          const count = counts?.[f.key] ?? 0;
          return (
            <button
              key={`status-${f.key}`}
              className={`pos-chip${filterType === f.key ? ' active' : ''}`}
              onClick={() => setFilterType(prev => prev === f.key ? 'all' : f.key)}
            >
              {f.label}
              {count > 0 && (
                <span className="pos-chip-count">{count}</span>
              )}
            </button>
          );
        })}
        {PAYMENT_FILTERS_UI.map(f => {
          const count = counts?.[f.key] ?? 0;
          return (
            <button
              key={`pay-${f.key}`}
              className={`pos-chip${paymentFilter === f.key ? ' active' : ''}`}
              onClick={() => setPaymentFilter(prev => prev === f.key ? 'all' : f.key)}
            >
              {f.label}
              {count > 0 && (
                <span className="pos-chip-count">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Sale list */}
      {loading && <div className="pos-hint" style={{ marginTop: 24 }}>Cargando ventas…</div>}

      {!loading && (
        <div className="pos-sale-list">
          {displaySales.map(sale => {
            const saleDogs = getSaleDogs(sale);
            const dogId = saleDogs[0]?._id || saleDogs[0]?.id || '';
            const dogName = saleDogs.length > 0 ? saleDogs.map(d => d.name).join(' & ') : 'Público';
            const color = avatarColor(dogId);
            const itemsTxt = (sale.items || [])
              .map(it => `${(it.qty || 1) > 1 ? `${it.qty}× ` : ''}${it.name}`)
              .join(' · ');
            const status = sale.status || 'draft';
            const remaining = sale.remaining ?? Math.max(0, (sale.total || 0) - (sale.paid || 0));
            const whenStr = sale.createdAt ? formatRelativeDate(sale.createdAt) : '';

            const saleId = sale._id || sale.id;
            const saleLabel = sale.saleNumber
              ? `#${String(sale.saleNumber).padStart(4, '0')}`
              : `#${saleId?.slice(-6).toUpperCase()}`;

            const handlePrint = (e) => {
              e.stopPropagation();
              window.open(`/pos/${saleId}/receipt`, '_blank');
            };

            const handleShare = (e) => {
              e.stopPropagation();
              const payments = sale.payments ?? [];
              const { total: sTotal, remaining: sRemaining } = computeSale(sale.items || [], sale.discount ?? null, payments);
              const text = buildWhatsAppText(sale, payments, sTotal, sRemaining);
              const phone = getSaleDogs(sale)[0]?.owners?.[0]?.phone;
              const clean = phone ? phone.replace(/\D/g, '') : '';
              window.open(`https://wa.me/${clean}?text=${encodeURIComponent(text)}`, '_blank');
            };

            return (
              <div
                key={saleId}
                className="pos-sale-card"
                onClick={() => navigate(`/pos/${saleId}`)}
              >
                <div className="pos-sale-card-top">
                  <div className={`pos-customer-avatar ${color}`} style={{ width: 42, height: 42, fontSize: 20, borderRadius: 14 }}>
                    🐕
                  </div>
                  <div className="pos-sale-dog-info">
                    <div className="pos-sale-dog-name">{dogName}</div>
                    <div className="pos-sale-id-when">
                      {saleLabel}{whenStr ? ` · ${whenStr}` : ''}
                    </div>
                  </div>
                  <div className="pos-sale-right">
                    <div className="pos-sale-total-row">
                      <div className="pos-sale-total">{ILS(sale.total || 0)}</div>
                      <div className="pos-sale-icons">
                        <button className="pos-sale-icon-btn" onClick={handlePrint} title="Imprimir recibo">🖨️</button>
                        <button className="pos-sale-icon-btn" onClick={handleShare} title="Compartir por WhatsApp">📤</button>
                      </div>
                    </div>
                    <span className={`pos-pill pos-pill-${status}`}>
                      {status === 'partial' ? `${ILS(remaining)} pend.` : STATUS_LABELS[status] || status}
                    </span>
                  </div>
                </div>
                {itemsTxt && <div className="pos-sale-items-txt">{itemsTxt}</div>}
              </div>
            );
          })}
          {displaySales.length === 0 && (
            <div className="pos-hint" style={{ paddingTop: 32 }}>
              {searchQuery.trim()
                ? `Sin resultados para "${searchQuery}".`
                : isStatusFiltered
                  ? filterType === 'due'     ? 'Sin ventas pendientes de pago.'
                  : filterType === 'partial' ? 'Sin ventas con pago parcial.'
                  : filterType === 'paid'    ? 'Sin ventas pagadas.'
                  : 'Sin ventas.'
                : isToday
                  ? <>Sin ventas hoy.{' '}<button style={{ color: 'var(--pos-accent,#7B4BD9)', fontWeight: 800, background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }} onClick={() => navigate('/pos/new')}>Crear primera venta →</button></>
                  : 'Sin ventas este día.'
              }
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
          <button 
            onClick={() => setPage(p => Math.max(1, p - 1))} 
            disabled={page === 1}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e0e0e0', background: page === 1 ? '#f5f5f5' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
          >
            Anterior
          </button>
          <span style={{ fontWeight: 'bold' }}>Página {page} de {totalPages}</span>
          <button 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
            disabled={page === totalPages}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e0e0e0', background: page === totalPages ? '#f5f5f5' : '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
